import {
  BoxGeometry,
  BufferGeometry,
  CircleGeometry,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Matrix4,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import { ROAD_SIGN_RENDER_CONFIG } from "../../config/roadSigns.ts";
import { pointInPolygon } from "../../world/geometry.ts";
import {
  pedestrianCrossingSourcesForTile,
  type TilePedestrianCrossingSource,
} from "../../world/pedestrianCrossings.ts";
import type { RuntimeLinearFeature } from "../../world/schema/runtimeTile.ts";
import type { RuntimeWorldTilePayloadV2 } from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import {
  isPedestrianStreetFeature,
  streetFeatureRoadEdgeWidthM,
  streetFeatureSurfaceElevationM,
} from "../../world/streets.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import { createRegulatoryRoadSignBatch } from "./regulatoryRoadSigns.ts";

export interface RoadSignBatch {
  readonly object: Group;
  readonly crossingCount: number;
  readonly signCount: number;
  readonly skippedCrossingCount: number;
}

interface RoadProjection {
  readonly road: Readonly<RuntimeLinearFeature>;
  readonly x: number;
  readonly z: number;
  readonly tangentX: number;
  readonly tangentZ: number;
}

export interface PedestrianCrossingSignPlacement {
  readonly source: Readonly<TilePedestrianCrossingSource>;
  readonly roadId: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly side: -1 | 1;
  readonly fallbackStep: number;
  readonly roadsideFallbackStep: number;
  readonly edgeGroundingFallback: boolean;
}

export interface RoadSignPlacementDiagnostics {
  readonly sourceCrossingCount: number;
  readonly projectedCrossingCount: number;
  readonly placedCrossingCount: number;
  readonly signCount: number;
  readonly pairedDualCarriagewaySystemCount: number;
  readonly expectedSignCount: number;
  readonly edgeGroundingFallbackCount: number;
  readonly maximumAlongFallbackM: number;
  readonly maximumRoadsideFallbackM: number;
}

const SIGN_GROUND_LIFT_M = 0.012;
const CARRIAGEWAY_CLEARANCE_EPSILON_M = 0.05;
const CROSSING_SIGN_FALLBACKS = crossingSignFallbacks();

export function createRoadSignBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  materials: CityMaterialLibrary,
): RoadSignBatch | null {
  const sources = pedestrianCrossingSourcesForTile(tile);
  const placements = pedestrianCrossingSignPlacementsForTile(tile, terrain);
  const regulatory = createRegulatoryRoadSignBatch(tile, terrain, materials);
  if (placements.length === 0 && regulatory === null) {
    return null;
  }
  const renderedCrossingCount = new Set(
    placements.map((placement) => placement.source.id),
  ).size;
  const object = new Group();
  object.name = `runtime-road-signs:${tile.id}`;
  object.userData = {
    tileId: tile.id,
    sourceLayers: [
      "openstreetmap-road-controls",
      "via-lietuva-state-road-sign-inventory",
    ],
    sourceLicenses: ["ODbL-1.0", "CC-BY-4.0"],
    renderedCrossingCount,
    renderedSignCount: placements.length + (regulatory?.signCount ?? 0),
    skippedCrossingCount: sources.length - renderedCrossingCount,
    placement:
      "source-crossing-strict-grpk-axis-match-real-road-width-right-hand-approaches",
    representation:
      "lithuanian-533-534-crossings-and-directional-regulatory-signs-low-poly",
  };

  const parts = [
    {
      name: "poles-and-brackets",
      geometry: createPoleGeometry(),
      material: materials.streetPole,
    },
    {
      name: "blue-plates",
      geometry: createPlateGeometry(),
      material: materials.roadSignBlue,
    },
    {
      name: "reflective-triangles",
      geometry: createTriangleGeometry(),
      material: materials.roadSignIvory,
    },
    {
      name: "pedestrian-symbols",
      geometry: createPedestrianGeometry(),
      material: materials.roadSignInk,
    },
  ] as const;
  const matrix = new Matrix4();
  for (const part of placements.length === 0 ? [] : parts) {
    const instances = new InstancedMesh(
      part.geometry,
      part.material,
      placements.length,
    );
    instances.name = `${object.name}:${part.name}`;
    instances.castShadow = false;
    instances.receiveShadow = false;
    for (const [index, placement] of placements.entries()) {
      matrix.makeRotationY(placement.yaw);
      matrix.setPosition(placement.x, placement.y, placement.z);
      instances.setMatrixAt(index, matrix);
    }
    instances.instanceMatrix.needsUpdate = true;
    object.add(instances);
  }
  if (regulatory !== null) object.add(regulatory.object);
  return {
    object,
    crossingCount: renderedCrossingCount,
    signCount: placements.length + (regulatory?.signCount ?? 0),
    skippedCrossingCount: sources.length - renderedCrossingCount,
  };
}

export function pedestrianCrossingSignPlacementsForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData> = tile.terrain,
): readonly Readonly<PedestrianCrossingSignPlacement>[] {
  return roadSignPlacementResultForTile(tile, terrain).placements;
}

export function roadSignPlacementDiagnosticsForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData> = tile.terrain,
): Readonly<RoadSignPlacementDiagnostics> {
  const result = roadSignPlacementResultForTile(tile, terrain);
  const pairedDualCarriagewaySystemCount =
    pairedDualCarriagewaySignSystemCount(
      pedestrianCrossingSourcesForTile(tile),
      tile.transportLines.filter((feature) => feature.kind === "road"),
      result.placements,
    );
  return {
    sourceCrossingCount: result.sourceCrossingCount,
    projectedCrossingCount: result.projectedCrossingCount,
    placedCrossingCount: new Set(
      result.placements.map((placement) => placement.source.id),
    ).size,
    signCount: result.placements.length,
    pairedDualCarriagewaySystemCount,
    expectedSignCount:
      result.sourceCrossingCount * 2 -
      pairedDualCarriagewaySystemCount * 2,
    edgeGroundingFallbackCount: result.placements.filter((placement) =>
      placement.edgeGroundingFallback
    ).length,
    maximumAlongFallbackM: Math.max(
      0,
      ...result.placements.map((placement) =>
        placement.fallbackStep * ROAD_SIGN_RENDER_CONFIG.fallbackAlongStepM
      ),
    ),
    maximumRoadsideFallbackM: Math.max(
      0,
      ...result.placements.map((placement) =>
        Math.abs(placement.roadsideFallbackStep) *
          ROAD_SIGN_RENDER_CONFIG.fallbackRoadsideStepM
      ),
    ),
  };
}

function pairedDualCarriagewaySignSystemCount(
  sources: readonly Readonly<TilePedestrianCrossingSource>[],
  roads: readonly Readonly<RuntimeLinearFeature>[],
  placements: readonly Readonly<PedestrianCrossingSignPlacement>[],
): number {
  const used = new Set<string>();
  let count = 0;
  for (const source of sources) {
    if (used.has(source.id)) continue;
    const sourcePlacements = placements.filter((placement) =>
      placement.source.id === source.id
    );
    const sourceRoad = roads.find((road) =>
      sourceFeatureId(road) === source.roadFeatureId
    );
    if (sourcePlacements.length !== 1 || sourceRoad === undefined) continue;
    const sourceName = sourceRoad.name?.trim().toLocaleLowerCase("lt-LT");
    if (!sourceName) continue;
    const partner = sources.find((candidate) => {
      if (candidate.id === source.id || used.has(candidate.id)) return false;
      const candidatePlacements = placements.filter((placement) =>
        placement.source.id === candidate.id
      );
      const candidateRoad = roads.find((road) =>
        sourceFeatureId(road) === candidate.roadFeatureId
      );
      if (candidatePlacements.length !== 1 || candidateRoad === undefined) {
        return false;
      }
      const candidateName = candidateRoad.name?.trim()
        .toLocaleLowerCase("lt-LT");
      const centreDistanceM = Math.hypot(
        source.roadPosition.x - candidate.roadPosition.x,
        source.roadPosition.z - candidate.roadPosition.z,
      );
      const maximumSharedMedianDistanceM =
        (streetFeatureRoadEdgeWidthM(sourceRoad) +
          streetFeatureRoadEdgeWidthM(candidateRoad)) / 2 + 1.5;
      const tangentAgreement = Math.abs(
        source.tangent.x * candidate.tangent.x +
          source.tangent.z * candidate.tangent.z,
      );
      return candidateName === sourceName &&
        centreDistanceM <= maximumSharedMedianDistanceM &&
        tangentAgreement >= Math.cos(Math.PI * 15 / 180) &&
        candidatePlacements[0]!.side !== sourcePlacements[0]!.side;
    });
    if (partner === undefined) continue;
    used.add(source.id);
    used.add(partner.id);
    count += 1;
  }
  return count;
}

function roadSignPlacementResultForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
): Readonly<{
  sourceCrossingCount: number;
  projectedCrossingCount: number;
  placements: readonly Readonly<PedestrianCrossingSignPlacement>[];
}> {
  const roads = tile.transportLines.filter((feature) => feature.kind === "road");
  const matchingRoads = roads.filter((feature) =>
    !isPedestrianStreetFeature(feature)
  );
  const sources = pedestrianCrossingSourcesForTile(tile);
  let projectedCrossingCount = 0;
  const placements = sources.flatMap((source) => {
    const projection = offlineRoadProjection(source, matchingRoads);
    if (projection === null) {
      return [];
    }
    projectedCrossingCount += 1;
    return ([-1, 1] as const).flatMap((side) => {
      for (const fallback of CROSSING_SIGN_FALLBACKS) {
        const placement = placementForSide(
          source,
          projection,
          side,
          fallback.alongStep,
          fallback.roadsideStep,
          tile,
          terrain,
        );
        if (placement !== null && placementIsClear(placement, tile, roads)) {
          return [placement];
        }
      }
      return [];
    });
  });
  return {
    sourceCrossingCount: sources.length,
    projectedCrossingCount,
    placements,
  };
}

function offlineRoadProjection(
  source: Readonly<TilePedestrianCrossingSource>,
  roads: readonly Readonly<RuntimeLinearFeature>[],
): RoadProjection | null {
  const road = roads.find((candidate) =>
    sourceFeatureId(candidate) === source.roadFeatureId
  );
  return road === undefined
    ? null
    : {
        road,
        x: source.roadPosition.x,
        z: source.roadPosition.z,
        tangentX: source.tangent.x,
        tangentZ: source.tangent.z,
      };
}

function placementForSide(
  source: Readonly<TilePedestrianCrossingSource>,
  projection: Readonly<RoadProjection>,
  side: -1 | 1,
  fallbackStep: number,
  roadsideFallbackStep: number,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
): PedestrianCrossingSignPlacement | null {
  const normalX = projection.tangentZ;
  const normalZ = -projection.tangentX;
  const roadsideOffsetM = streetFeatureRoadEdgeWidthM(projection.road) / 2 +
    ROAD_SIGN_RENDER_CONFIG.curbClearanceM +
    roadsideFallbackStep * ROAD_SIGN_RENDER_CONFIG.fallbackRoadsideStepM;
  const alongOffsetM = -side * (
    ROAD_SIGN_RENDER_CONFIG.crossingHalfLengthM +
    ROAD_SIGN_RENDER_CONFIG.crossingEdgeClearanceM +
    fallbackStep * ROAD_SIGN_RENDER_CONFIG.fallbackAlongStepM
  );
  const x = projection.x + projection.tangentX * alongOffsetM +
    normalX * roadsideOffsetM * side;
  const z = projection.z + projection.tangentZ * alongOffsetM +
    normalZ * roadsideOffsetM * side;
  const directSurfaceY = streetFeatureSurfaceElevationM(
    projection.road,
    terrain,
    tile.origin,
    { x, z },
  );
  const surfaceY = directSurfaceY ?? streetFeatureSurfaceElevationM(
    projection.road,
    terrain,
    tile.origin,
    {
      x: Math.max(0, Math.min(tile.sizeM, x)),
      z: Math.max(0, Math.min(tile.sizeM, z)),
    },
  );
  if (surfaceY === null) {
    return null;
  }
  // Lithuania drives on the right. Each diagonal sign faces its approaching
  // lane, matching the paired 533/534 crossing-sign arrangement.
  const frontX = -projection.tangentX * side;
  const frontZ = -projection.tangentZ * side;
  return {
    source,
    roadId: projection.road.id,
    x,
    y: surfaceY + SIGN_GROUND_LIFT_M,
    z,
    yaw: Math.atan2(frontX, frontZ),
    side,
    fallbackStep,
    roadsideFallbackStep,
    edgeGroundingFallback: directSurfaceY === null,
  };
}

function crossingSignFallbacks(): readonly Readonly<{
  alongStep: number;
  roadsideStep: number;
}>[] {
  const config = ROAD_SIGN_RENDER_CONFIG;
  const roadsideSteps = [
    0,
    ...Array.from(
      { length: config.fallbackRoadsideOutwardAttemptCount },
      (_, index) => index + 1,
    ),
    ...Array.from(
      { length: config.fallbackRoadsideInwardAttemptCount },
      (_, index) => -(index + 1),
    ),
  ];
  const candidates = Array.from(
    {
      length:
        config.fallbackAttemptCount * roadsideSteps.length,
    },
    (_, index) => ({
      alongStep: index % config.fallbackAttemptCount,
      roadsideStep:
        roadsideSteps[Math.floor(index / config.fallbackAttemptCount)]!,
    }),
  );
  candidates.sort((a, b) => {
    const aDistanceM = Math.hypot(
      a.alongStep * config.fallbackAlongStepM,
      a.roadsideStep * config.fallbackRoadsideStepM,
    );
    const bDistanceM = Math.hypot(
      b.alongStep * config.fallbackAlongStepM,
      b.roadsideStep * config.fallbackRoadsideStepM,
    );
    return aDistanceM - bDistanceM ||
      Math.abs(a.roadsideStep) - Math.abs(b.roadsideStep) ||
      b.roadsideStep - a.roadsideStep ||
      a.alongStep - b.alongStep;
  });
  return candidates;
}

function placementIsClear(
  placement: Readonly<PedestrianCrossingSignPlacement>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  roads: readonly Readonly<RuntimeLinearFeature>[],
): boolean {
  const config = ROAD_SIGN_RENDER_CONFIG;
  if (tile.buildings.some((building) =>
    pointInPolygon(placement, building.footprint) ||
    distanceToPolygonEdges(placement, building.footprint.outer) <
      config.buildingClearanceM
  )) {
    return false;
  }
  if ((tile.vegetation ?? []).some((tree) =>
    Math.hypot(
      placement.x - tree.position.x,
      placement.z - tree.position.z,
    ) < config.treeClearanceM + Math.max(0.08, tree.trunkDiameterM ?? 0) / 2
  )) {
    return false;
  }
  return !roads.some((road) =>
    distanceToPolyline(placement, road.points) <
      streetFeatureRoadEdgeWidthM(road) / 2 +
        CARRIAGEWAY_CLEARANCE_EPSILON_M
  );
}

function createPoleGeometry(): BufferGeometry {
  const config = ROAD_SIGN_RENDER_CONFIG;
  return mergeOwned([
    translated(
      new CylinderGeometry(
        config.poleRadiusM * 1.55,
        config.poleRadiusM * 1.85,
        0.11,
        8,
      ),
      0,
      0.055,
      0,
    ),
    translated(
      new CylinderGeometry(
        config.poleRadiusM,
        config.poleRadiusM * 1.08,
        config.poleHeightM,
        8,
      ),
      0,
      config.poleHeightM / 2,
      0,
    ),
    translated(
      new BoxGeometry(0.1, 0.065, config.plateDepthM + 0.05),
      0,
      config.plateCenterHeightM - config.plateHeightM / 2 + 0.08,
      0,
    ),
    translated(
      new BoxGeometry(0.1, 0.065, config.plateDepthM + 0.05),
      0,
      config.plateCenterHeightM + config.plateHeightM / 2 - 0.08,
      0,
    ),
  ], "vilnius-crossing-sign:pole-and-brackets");
}

function createPlateGeometry(): BufferGeometry {
  const config = ROAD_SIGN_RENDER_CONFIG;
  const geometry = new BoxGeometry(
    config.plateWidthM,
    config.plateHeightM,
    config.plateDepthM,
  );
  geometry.translate(0, config.plateCenterHeightM, 0);
  geometry.name = "vilnius-crossing-sign:blue-square-plate";
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createTriangleGeometry(): BufferGeometry {
  const config = ROAD_SIGN_RENDER_CONFIG;
  const centreY = config.plateCenterHeightM - 0.015;
  const halfWidthM = config.plateWidthM * 0.355;
  const halfHeightM = config.plateHeightM * 0.355;
  const faceZ = config.plateDepthM / 2 + 0.004;
  const positions = [
    0, centreY + halfHeightM, faceZ,
    -halfWidthM, centreY - halfHeightM, faceZ,
    halfWidthM, centreY - halfHeightM, faceZ,
    0, centreY + halfHeightM, -faceZ,
    halfWidthM, centreY - halfHeightM, -faceZ,
    -halfWidthM, centreY - halfHeightM, -faceZ,
  ];
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setIndex([0, 1, 2, 3, 4, 5]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.name = "vilnius-crossing-sign:reflective-triangle";
  return geometry;
}

function createPedestrianGeometry(): BufferGeometry {
  const config = ROAD_SIGN_RENDER_CONFIG;
  const centreY = config.plateCenterHeightM - 0.055;
  const faceZ = config.plateDepthM / 2 + 0.009;
  const parts: BufferGeometry[] = [];
  for (const z of [faceZ, -faceZ]) {
    const facing = z > 0 ? 1 : -1;
    const head = new CircleGeometry(0.055, 9);
    if (facing < 0) {
      head.rotateY(Math.PI);
    }
    head.translate(-0.055, centreY + 0.145, z);
    parts.push(
      head,
      signGlyphBar(0.075, 0.19, -0.012, centreY + 0.025, z, -0.2),
      signGlyphBar(0.045, 0.19, -0.085, centreY - 0.13, z, -0.62),
      signGlyphBar(0.045, 0.205, 0.062, centreY - 0.13, z, 0.66),
      signGlyphBar(0.04, 0.17, -0.105, centreY + 0.03, z, -0.82),
      signGlyphBar(0.04, 0.17, 0.085, centreY + 0.035, z, 0.78),
    );
  }
  return mergeOwned(parts, "vilnius-crossing-sign:walking-person-symbol");
}

function signGlyphBar(
  width: number,
  height: number,
  x: number,
  y: number,
  z: number,
  rotationZ: number,
): BufferGeometry {
  const geometry = new BoxGeometry(width, height, 0.012);
  geometry.rotateZ(rotationZ);
  geometry.translate(x, y, z);
  return geometry;
}

function translated(
  geometry: BufferGeometry,
  x: number,
  y: number,
  z: number,
): BufferGeometry {
  geometry.translate(x, y, z);
  return geometry;
}

function mergeOwned(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries]);
  for (const geometry of geometries) {
    geometry.dispose();
  }
  if (merged === null) {
    throw new Error(`Could not merge ${name}`);
  }
  merged.name = name;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

function sourceFeatureId(feature: Readonly<RuntimeLinearFeature>): string {
  return feature.id.split("#", 1)[0] ?? feature.id;
}

function distanceToPolyline(
  point: Readonly<{ x: number; z: number }>,
  line: readonly Readonly<{ x: number; z: number }>[],
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < line.length; index += 1) {
    const from = line[index - 1];
    const to = line[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquaredM = deltaX * deltaX + deltaZ * deltaZ;
    const t = lengthSquaredM <= 0
      ? 0
      : Math.max(0, Math.min(1,
        ((point.x - from.x) * deltaX +
          (point.z - from.z) * deltaZ) / lengthSquaredM,
      ));
    minimum = Math.min(minimum, Math.hypot(
      point.x - (from.x + deltaX * t),
      point.z - (from.z + deltaZ * t),
    ));
  }
  return minimum;
}

function distanceToPolygonEdges(
  point: Readonly<{ x: number; z: number }>,
  ring: readonly Readonly<{ x: number; z: number }>[],
): number {
  if (ring.length < 2) {
    return Number.POSITIVE_INFINITY;
  }
  return distanceToPolyline(point, [...ring, ring[0]!]);
}
